import {
  CloudflareCountryCache,
  RegisterExperimentBucket,
  RegisterVariant,
  SessionStorageData,
  SignUpSessionData,
} from "./types";

import { GoogleSSOSignUpData } from "@/features/Auth/Register/store/GoogleSSOSignUpData";
import { RegisterCategory } from "@/features/Auth/Register/types";

const STORAGE_KEY = "zeffy_signup_data";

const isBrowser = typeof window !== "undefined";

export const SignUpSessionStorage = {
  save(data: SignUpSessionData): void {
    if (!isBrowser) {
      return;
    }

    try {
      // Get full existing storage data to preserve registerVariant and registerCategory
      const existingStorage = sessionStorage.getItem(STORAGE_KEY);
      const existingParsed: SessionStorageData = existingStorage ? JSON.parse(existingStorage) : { signUpData: {} };
      const existingSignUpData = existingParsed.signUpData || {};

      const mergedData: SessionStorageData = {
        ...existingParsed, // Preserve registerVariant, registerCategory, googleSSOData
        signUpData: { ...existingSignUpData, ...data },
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(mergedData));
    } catch {
      // Silently ignore storage errors (e.g., quota exceeded, private browsing)
    }
  },

  get(): SignUpSessionData {
    if (!isBrowser) {
      return {};
    }

    try {
      const data = sessionStorage.getItem(STORAGE_KEY);
      if (!data) {
        return {};
      }

      const parsed: SessionStorageData = JSON.parse(data);
      return parsed.signUpData || {};
    } catch {
      return {};
    }
  },

  update(data: Partial<SignUpSessionData>): void {
    if (!isBrowser) {
      return;
    }

    try {
      const existingData = this.get();
      this.save({ ...existingData, ...data });
    } catch {
      // Silently ignore storage errors
    }
  },

  clear(): void {
    if (!isBrowser) {
      return;
    }

    try {
      const existingStorage = sessionStorage.getItem(STORAGE_KEY);
      if (existingStorage) {
        const existingParsed: SessionStorageData = JSON.parse(existingStorage);
        const clearedData: SessionStorageData = {
          signUpData: {},
          registerVariant: existingParsed.registerVariant,
          registerCategory: existingParsed.registerCategory,
          registerExperiments: existingParsed.registerExperiments,
          cloudflareCountry: existingParsed.cloudflareCountry,
          signUpSource: existingParsed.signUpSource,
        };
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(clearedData));
      } else {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // Silently ignore storage errors
    }
  },

  saveGoogleSSOData(data: GoogleSSOSignUpData): void {
    if (!isBrowser) {
      return;
    }

    try {
      // Get full existing storage data to preserve registerVariant and registerCategory
      const existingStorage = sessionStorage.getItem(STORAGE_KEY);
      const existingParsed: SessionStorageData = existingStorage ? JSON.parse(existingStorage) : { signUpData: {} };
      const existingSignUpData = existingParsed.signUpData || {};

      const storageData: SessionStorageData = {
        ...existingParsed, // Preserve registerVariant, registerCategory
        signUpData: {
          ...existingSignUpData,
          email: data.email,
          firstName: data.firstName,
          lastName: data.lastName,
          ssoStytchData: {
            stytchUserId: data.stytchUserId,
            stytchOrganizationId: data.stytchOrganizationId,
            stytchSessionJwt: data.stytchSessionJwt,
            stytchSessionToken: data.stytchSessionToken,
            provider: "google",
          },
        },
        googleSSOData: data,
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(storageData));
    } catch {
      // Silently ignore storage errors
    }
  },

  getGoogleSSOData(): GoogleSSOSignUpData | null {
    if (!isBrowser) {
      return null;
    }

    try {
      const data = sessionStorage.getItem(STORAGE_KEY);
      if (!data) {
        return null;
      }

      const parsed: SessionStorageData = JSON.parse(data);
      return parsed.googleSSOData || null;
    } catch {
      return null;
    }
  },

  hasData(): boolean {
    if (!isBrowser) {
      return false;
    }

    try {
      const data = sessionStorage.getItem(STORAGE_KEY);
      return !!data;
    } catch {
      return false;
    }
  },

  getRegisterVariant(): RegisterVariant | undefined {
    if (!isBrowser) {
      return undefined;
    }

    try {
      const data = sessionStorage.getItem(STORAGE_KEY);
      if (!data) {
        return undefined;
      }

      const parsed: SessionStorageData = JSON.parse(data);
      return parsed.registerVariant;
    } catch {
      return undefined;
    }
  },

  setRegisterVariant(variant: RegisterVariant): void {
    if (!isBrowser) {
      return;
    }

    try {
      const data = sessionStorage.getItem(STORAGE_KEY);
      const parsed: SessionStorageData = data ? JSON.parse(data) : { signUpData: {} };
      parsed.registerVariant = variant;
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
    } catch {
      // Silently ignore storage errors
    }
  },

  getRegisterCategory(): RegisterCategory | undefined {
    if (!isBrowser) {
      return undefined;
    }

    try {
      const data = sessionStorage.getItem(STORAGE_KEY);
      if (!data) {
        return undefined;
      }

      const parsed: SessionStorageData = JSON.parse(data);
      return parsed.registerCategory;
    } catch {
      return undefined;
    }
  },

  setRegisterCategory(category: RegisterCategory): void {
    if (!isBrowser) {
      return;
    }

    try {
      const data = sessionStorage.getItem(STORAGE_KEY);
      const parsed: SessionStorageData = data ? JSON.parse(data) : { signUpData: {} };
      parsed.registerCategory = category;
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
    } catch {
      // Silently ignore storage errors
    }
  },

  getRegisterExperiment(experimentKey: string): RegisterExperimentBucket | undefined {
    if (!isBrowser) {
      return undefined;
    }

    try {
      const data = sessionStorage.getItem(STORAGE_KEY);
      if (!data) {
        return undefined;
      }

      const parsed: SessionStorageData = JSON.parse(data);
      return parsed.registerExperiments?.[experimentKey];
    } catch {
      return undefined;
    }
  },

  setRegisterExperiment(experimentKey: string, value: RegisterExperimentBucket): void {
    if (!isBrowser) {
      return;
    }

    try {
      const data = sessionStorage.getItem(STORAGE_KEY);
      const parsed: SessionStorageData = data ? JSON.parse(data) : { signUpData: {} };
      parsed.registerExperiments = { ...parsed.registerExperiments, [experimentKey]: value };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
    } catch {
      // Silently ignore storage errors
    }
  },

  getCloudflareCountry(): CloudflareCountryCache | undefined {
    if (!isBrowser) {
      return undefined;
    }

    try {
      const data = sessionStorage.getItem(STORAGE_KEY);
      if (!data) {
        return undefined;
      }

      const parsed: SessionStorageData = JSON.parse(data);
      return parsed.cloudflareCountry;
    } catch {
      return undefined;
    }
  },

  setCloudflareCountry(country: CloudflareCountryCache): void {
    if (!isBrowser) {
      return;
    }

    try {
      const data = sessionStorage.getItem(STORAGE_KEY);
      const parsed: SessionStorageData = data ? JSON.parse(data) : { signUpData: {} };
      parsed.cloudflareCountry = country;
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
    } catch {
      // Silently ignore storage errors
    }
  },

  getSignUpSource(): string | undefined {
    if (!isBrowser) {
      return undefined;
    }

    try {
      const data = sessionStorage.getItem(STORAGE_KEY);
      if (!data) {
        return undefined;
      }

      const parsed: SessionStorageData = JSON.parse(data);
      return parsed.signUpSource;
    } catch {
      return undefined;
    }
  },

  setSignUpSource(signUpSource: string): void {
    if (!isBrowser) {
      return;
    }

    try {
      const data = sessionStorage.getItem(STORAGE_KEY);
      const parsed: SessionStorageData = data ? JSON.parse(data) : { signUpData: {} };
      parsed.signUpSource = signUpSource;
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
    } catch {
      // Silently ignore storage errors
    }
  },
};
